import { useState, useEffect, useCallback } from 'react';
import { fileService } from '@/features/files/services/fileService';
import { FileMetadata } from '@/features/files/types/file.types';
import { useTaxYearStore } from '@/features/tax-year/store/taxYearStore';

export interface DashboardStats {
  totalDocuments: number;
  recentDocuments: FileMetadata[];
}

export const useDashboardStats = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalDocuments: 0,
    recentDocuments: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const effectiveYear = useTaxYearStore((s) => s.getEffectiveYear());
  const configFetched = useTaxYearStore((s) => s.configFetched);

  const fetchStats = useCallback(async () => {
    if (effectiveYear == null) return;
    setIsLoading(true);
    setError(null);

    try {
      const docsResponse = await fileService.getDocuments(effectiveYear);

      setStats({
        totalDocuments: docsResponse.total ?? docsResponse.documents.length,
        recentDocuments: docsResponse.documents.slice(0, 5),
      });
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard stats error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveYear]);

  useEffect(() => {
    if (!configFetched || effectiveYear == null) return;
    fetchStats();
  }, [configFetched, effectiveYear, fetchStats]);

  return {
    stats,
    isLoading: isLoading || !configFetched,
    error,
    refreshStats: fetchStats,
  };
};
